<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { $api } from '@/services';
import DaModalFrame from '@/components/common/daModalFrame/DaModalFrame.vue';
import CreateForm from './CreateFormModal.vue';
import DeleteForm from './DeleteForm.vue';
import LoadingDowloadPDF from '@/views/plansDetail/features/reportTarget/loadingDowloadPDF/loadingDowloadPDF.vue';
// import TooltipGoalDetail from '@/components/elements/tooltipGoalDetail/TooltipGoalDetail';

const store = useStore();
const route = useRoute();
const router = useRouter();
const formData = ref({});
const skills = ref([]);
const stats = ref({});
const isLoading = ref(true);
const openMenu = ref(false);
const modalIsOpen = ref(false);
const type = ref('');
const inProgress = ref(false);
const isReference = computed(() => route.query.is_reference === 'true');
const formId = computed(() => formData.value?.id || route.params.id);
const createdByName = computed(
	() =>
		formData.value?.created_by_staff?.full_name ||
		formData.value?.created_by_staff?.name ||
		''
);

const closeMenu = () => {
	openMenu.value = false;
};

onMounted(() => {
	fetchData();
	document.addEventListener('click', closeMenu);
});

onBeforeUnmount(() => {
	document.removeEventListener('click', closeMenu);
});

const openModal = (modalType) => {
	type.value = modalType;
	modalIsOpen.value = true;
	openMenu.value = false;
};

const closeModal = () => {
	const shouldRefetch = type.value === 'edit';
	modalIsOpen.value = false;
	type.value = '';
	if (shouldRefetch) fetchData();
};

const fetchData = async () => {
	try {
		const res = await $api.form.detail(route.params.id, isReference.value);
		formData.value = res?.data?.data || {};
		skills.value = res?.data?.skills || [];
		stats.value = res?.data?.stats || {};
	} catch (err) {
		await store.dispatch('setAlertMessage', {
			message:
				err?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	} finally {
		isLoading.value = false;
	}
};

const duplicateForm = async () => {
	if (inProgress.value || !formId.value) return;
	openMenu.value = false;
	inProgress.value = true;
	try {
		const res = await $api.form.duplicateForm(
			formId.value,
			isReference.value
		);
		if (!res) return;
		await store.dispatch('setAlertMessage', {
			message: 'Tạo bản sao biểu mẫu thành công',
			type: 'success',
		});
	} catch (err) {
		await store.dispatch('setAlertMessage', {
			message:
				err?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	} finally {
		inProgress.value = false;
	}
};

const deleteFormEvaluation = async () => {
	if (inProgress.value || !formId.value) return;
	inProgress.value = true;
	try {
		const res = await $api.form.deleteForm(formId.value);
		if (!res) return;
		await store.dispatch('setAlertMessage', {
			message: 'Xoá biểu mẫu thành công',
			type: 'success',
		});
		modalIsOpen.value = false;
		type.value = '';
		router.push('/form-evaluation?tab=center');
	} catch (err) {
		await store.dispatch('setAlertMessage', {
			message:
				err?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	} finally {
		inProgress.value = false;
	}
};

const downloadPDF = async () => {
	if (!formId.value) return;
	const referenceQuery = isReference.value ? '&is_reference=true' : '';
	const url =
		process.env.VUE_APP_PDF_URL +
		'form-evaluation/' +
		formId.value +
		'?token=' +
		localStorage.getItem('tk') +
		'&subdomain=' +
		localStorage.getItem('sub-domain') +
		referenceQuery;
	const params = {
		url: url,
		format: 'a2',
		id_render: 'form_pdf',
	};
	try {
		openMenu.value = false;
		type.value = 'loading';
		modalIsOpen.value = true;
		const response = await fetch(
			'https://g4ridpplxi.execute-api.ap-southeast-1.amazonaws.com/',
			{
				method: 'POST',
				body: JSON.stringify(params),
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			}
		);
		if (!response.ok) {
			window.location.href = url + '&is_print=true';
			closeModal();
			return;
		}
		const data = await response.json();
		if (data.data) {
			closeModal();
			const linkSource = `data:application/pdf;base64, ${data.data}`;
			const link = document.createElement('a');
			const fileName = 'BieuMauDanhGia.pdf';
			link.href = linkSource;
			link.download = fileName;
			link.click();
		}
	} catch (e) {
		window.location.href = url + '&is_print=true';
		closeModal();
		console.log(e);
	}
};
</script>
<template>
	<DaModalFrame v-model:open="modalIsOpen">
		<CreateForm
			v-if="type === 'edit'"
			:form="formData"
			:open="modalIsOpen"
			type="edit"
			@close="closeModal"
		/>
		<DeleteForm
			v-if="type === 'delete'"
			@submit="deleteFormEvaluation"
			@close="closeModal"
		/>
		<LoadingDowloadPDF
			v-if="type === 'loading'"
			:text="'Đang tải xuống file PDF Biểu mẫu đánh giá...'"
		/>
	</DaModalFrame>
	<div class="px-12 md-down:px-4 pb-30">
		<div class="__report-lesson__header pt-10 pb-10">
			<div
				class="
					flex
					items-center
					gap-3
					mb-2
					text-neutral-500 text-sm
					font-semibold
				"
			>
				<div
					class="cursor-pointer"
					@click="
						$router.push(
							isReference
								? '/form-evaluation'
								: '/form-evaluation?tab=center'
						)
					"
				>
					Biểu mẫu đánh giá
				</div>
				<img alt="" src="@/assets/images/icons/arrow-right.png" />
				<div>{{ isReference ? 'Biểu mẫu tham khảo' : formData?.name }}</div>
			</div>
			<div class="flex flex-nowrap justify-between items-center mb-2">
				<div
					class="
						__record__title
						text-neutral-700
						font-semibold
						whitespace-nowrap
						flex flex-nowrap
						items-center
						text-4xl
						gap-4
					"
				>
					<svg
						class="cursor-pointer"
						width="28"
						height="28"
						viewBox="0 0 28 28"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						@click="
							isReference
								? $router.push('/form-evaluation')
								: $router.back()
						"
					>
						<path
							d="M17.5 7L10.5 14L17.5 21"
							stroke="#94A3B8"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
					<img
						src="@/assets/images/icons/form-evaluation.png"
						alt=""
					/>
					<div>{{ formData?.name }}</div>
				</div>
				<div class="items-center flex gap-3">
					<button
						v-if="!isReference"
						class="button-p btn--ghost w-fit px-4"
						type="button"
						@click.stop="
							$router.push(
								`/form-evaluation/create/${formData?.id}`
							)
						"
					>
						Chỉnh sửa
					</button>
					<div class="flex justify-center items-center relative">
						<img
							class="w-6 h-6 cursor-pointer"
							src="@/assets/images/icons/three-dots.png"
							alt=""
							@click.stop="openMenu = !openMenu"
						/>
						<div
							v-if="openMenu"
							class="absolute menu-action py-1 z-10"
						>
							<div
								class="
									flex
									items-center
									gap-3
									px-4
									py-2
									hover:bg-orange-50
									cursor-pointer
								"
								@click.stop="duplicateForm"
							>
								<img
									class="w-4 h-4"
									src="@/assets/images/icons/copy.png"
									alt=""
								/>
								<div class="text-neutral-700">Tạo bản sao</div>
							</div>
							<div
								v-if="!isReference"
								class="
									flex
									items-center
									gap-3
									px-4
									py-2
									hover:bg-orange-50
									cursor-pointer
								"
								@click.stop="openModal('edit')"
							>
								<img
									class="w-4 h-4"
									src="@/assets/images/icons/pen.png"
									alt=""
								/>
								<div class="text-neutral-700">Đổi tên</div>
							</div>
							<div
								class="
									flex
									items-center
									gap-3
									px-4
									py-2
									hover:bg-orange-50
									cursor-pointer
								"
								@click.stop="downloadPDF"
							>
								<img
									class="w-4 h-4"
									src="@/assets/images/icons/cloud-download-fill.png"
									alt=""
								/>
								<div class="text-neutral-700">Tải file pdf</div>
							</div>
							<div
								v-if="!isReference"
								class="
									flex
									items-center
									gap-3
									px-4
									py-2
									hover:bg-orange-50
									cursor-pointer
								"
								@click.stop="openModal('delete')"
							>
								<img
									class="w-4 h-4"
									src="@/assets/images/icons/trash.png"
									alt=""
								/>
								<div class="text-neutral-700">Xoá</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="text-green-500 text-xs font-semibold">
				TẠO BỞI
				<span class="text-blue-600 text-sm">{{ createdByName }}</span>
				| LÚC
				{{ $filters.formatDateTime(formData.inserted_at) }}
			</div>
		</div>
		<div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
			<div
				class="
					flex
					items-center
					justify-between
					bg-white
					py-4
					px-6
					rounded-lg
					shadow
				"
			>
				<div class="flex items-center gap-2">
					<img
						alt=""
						height="32"
						src="@/assets/images/icons/abc.png"
						style="height: 32px"
						width="32"
					/>
					<div class="text-neutral-500 text-sm">Lĩnh vực</div>
				</div>
				<div class="font-bold text-4xl text-neutral-600">
					{{ stats?.skill_count }}
				</div>
			</div>
			<div
				class="
					flex
					items-center
					justify-between
					bg-white
					py-4
					px-6
					rounded-lg
					shadow
				"
			>
				<div class="flex items-center gap-2">
					<img
						alt=""
						height="32"
						src="@/assets/images/icons/light.png"
						style="height: 32px"
						width="32"
					/>
					<div class="text-neutral-500 text-sm">Mục tiêu</div>
				</div>
				<div class="font-bold text-4xl text-neutral-600">
					{{ stats?.goal_count }}
				</div>
			</div>
		</div>
		<div v-for="(s, index) in skills" :key="index" class="mb-4">
			<div class="flex items-center justify-between mb-4">
				<div class="flex items-center">
					<div class="font-semibold text-neutral-700">
						{{ index + 1 }}. {{ s.skill?.name }} ({{
							s.library_items.length
						}})
					</div>
				</div>
			</div>
			<div
				class="shadow-sm sm:rounded-2xl overflow-hidden md-down:hidden"
			>
				<div class="__record__table overflow-x-auto scroll-bar">
					<div
						class="
							overflow-hidden
							sm:rounded-t-2xl
							w-fit
							min-w-full
						"
					>
						<table class="min-w-full divide-y text-neutral-200">
							<thead class="bg-neutral-100 h-16">
								<tr>
									<th
										class="
											px-4
											py-3
											text-center text-xs
											font-semibold
											text-neutral-600
											uppercase
											tracking-wider
											whitespace-nowrap
											w-16
										"
										scope="col"
									>
										STT
									</th>
									<th
										class="
											px-6
											py-3
											text-left text-xs
											font-semibold
											text-neutral-600
											uppercase
											tracking-wider
											whitespace-nowrap
										"
										scope="col"
									>
										Nội dung
									</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(i, gIndex) in s.library_items"
									:key="gIndex"
									:class="[
										gIndex % 2 === 0
											? 'bg-white'
											: 'bg-neutral-50',
									]"
									class="text-neutral-700 hover:bg-orange-50"
								>
									<td
										class="
											p-4
											whitespace-nowrap
											text-neutral-700 text-center
										"
									>
										{{ gIndex + 1 }}
									</td>
									<td
										class="
											px-6
											py-4
											whitespace-nowrap
											text-neutral-700
										"
									>
										<div class="flex items-center gap-2">
											<div
												class="whitespace-normal"
												v-html="i.name || i.goal_name"
											></div>
											<!-- <TooltipGoalDetail
												:goal-id="i.goal_id || i.id"
												:student-id="studentId"
											/> -->
										</div>
										<div
											class="flex gap-2 flex-wrap"
											style="max-width: 360px"
										>
											<template
												v-if="i?.assistance_levels"
											>
												<div
													v-for="ass in i?.assistance_levels"
													:key="ass"
													class="flex gap-2"
												>
													<div
														class="
															mt-1
															py-0.5
															px-2.5
															rounded-full
															bg-pink-100
															whitespace-nowrap
															text-neutral-700
															h-fit
															w-fit
															font-semibold
															shadow-input
														"
														style="font-size: 10px"
													>
														{{ ass }}
													</div>
												</div>
											</template>
											<div
												class="
													mt-1
													py-0.5
													px-2.5
													rounded-full
													bg-yellow-tag
													text-neutral-700
													h-fit
													w-fit
													font-semibold
													shadow-input
													whitespace-nowrap
												"
												style="font-size: 10px"
												v-if="i?.age"
											>
												{{ i?.age }}
											</div>
										</div>
										<div
											v-if="i.parent?.sub_goal_count > 0"
											class="goal-block flex gap-3 mt-2"
										>
											<img
												class="w-3 h-3 mt-1"
												src="@/assets/images/icons/goal-arrow.png"
												alt=""
											/>
											<div>
												<div
													class="
														text-neutral-700
														whitespace-normal
													"
													v-html="i.parent?.goal_name"
												></div>
												<div
													class="
														text-neutral-400 text-xs
														font-semibold
													"
												>
													{{
														i.parent?.sub_goal_count
													}}
													MỤC TIÊU
												</div>
											</div>
										</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<style scoped>
.menu-action {
	z-index: 100;
	top: 45px;
	right: 0px;
	border-radius: 6px;
	border: 1px solid #f9e3b7;
	background: #fff;
	width: 224px;
	box-shadow: 0px 10px 16px 0px rgba(20, 37, 63, 0.06),
		0px 0px 1px 0px rgba(12, 26, 75, 0.1);
}
</style>
